import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { INGEST_ALL_CONFIRMED_PREFIX } from './useIngestSession';

const mintPageKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * State for the chat-initiated bookings draft workflow (no service offer).
 *
 * Parallel to useIngestSession but simpler: no offer kickoff message,
 * no session reset, no reconciliation. Activates when the operator
 * types booking details into normal chat and the agent emits a
 * `bookings-proposal` block.
 *
 * The page_key is minted client-side the first time a proposal block
 * appears and stays stable across revisions. When the operator clicks
 * "Ingest all", the confirmation message carries this key so the agent
 * can call commit_bookings with the final rows.
 */
export default function useChatDraftSession({
  active,
  blocks,
  pending,
  send,
  scrollToLatest,
}) {
  // ── Latest proposal block (last-block-wins) ──
  const latestProposalBlock = useMemo(() => {
    if (!active) return null;
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b && b.kind === 'proposal' && b.proposal) return b;
    }
    return null;
  }, [active, blocks]);

  // ── Latest commit result ──
  const latestCommitResult = useMemo(() => {
    if (!active) return null;
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b && b.kind === 'commit_result' && b.result) return b.result;
    }
    return null;
  }, [active, blocks]);

  // ── Page key — minted once on first proposal, stable across revisions ──
  //
  // The agent generates its own page_key in the proposal JSON, but the
  // frontend mints an authoritative one for the `INGEST_ALL_CONFIRMED`
  // message and the idempotency key. This avoids trusting the agent's
  // generated string and keeps the pattern consistent with the offer flow.
  const pageKeyRef = useRef(null);
  const mintedRef = useRef(false);
  if (active && latestProposalBlock && !mintedRef.current) {
    pageKeyRef.current = mintPageKey();
    mintedRef.current = true;
  }
  if (!active) {
    pageKeyRef.current = null;
    mintedRef.current = false;
  }

  // ── Edited rows — seeded from latest proposal, reseeded on new block ──
  const [editedRows, setEditedRows] = useState([]);
  const seededFromBlockIdRef = useRef(null);
  useEffect(() => {
    if (!active || !latestProposalBlock) return;
    if (latestProposalBlock.id === seededFromBlockIdRef.current) return;
    seededFromBlockIdRef.current = latestProposalBlock.id;
    const rows = Array.isArray(latestProposalBlock.proposal.rows)
      ? latestProposalBlock.proposal.rows
      : [];
    setEditedRows(rows.map((r) => ({ ...r })));
  }, [active, latestProposalBlock]);

  useEffect(() => {
    if (active) return;
    setEditedRows([]);
    seededFromBlockIdRef.current = null;
  }, [active]);

  const handleRowEdit = useCallback((idx, patch) => {
    setEditedRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  // ── Edits summary (for the Discuss-changes diff) ──
  const editsSummary = useMemo(() => {
    if (!latestProposalBlock) return [];
    const original = latestProposalBlock.proposal?.rows || [];
    const diffs = [];
    editedRows.forEach((edited, idx) => {
      const orig = original[idx] || {};
      const changes = [];
      if (edited.full_name !== orig.full_name) {
        changes.push(`customer ${JSON.stringify(orig.full_name)} → ${JSON.stringify(edited.full_name)}`);
      }
      if (edited.date_datetime !== orig.date_datetime) {
        changes.push(`pickup ${orig.date_datetime} → ${edited.date_datetime}`);
      }
      const editedAdults = edited.passengers?.adults;
      const origAdults = orig.passengers?.adults;
      if (editedAdults !== origAdults) {
        changes.push(`passengers ${origAdults} → ${editedAdults}`);
      }
      if (edited.category?.name !== orig.category?.name) {
        changes.push(`vehicle ${JSON.stringify(orig.category?.name)} → ${JSON.stringify(edited.category?.name)}`);
      }
      if (edited.partner_id !== orig.partner_id) {
        changes.push(`partner_id ${orig.partner_id} → ${edited.partner_id}`);
      }
      if (edited.price !== orig.price) {
        changes.push(`price ${orig.price} → ${edited.price}`);
      }
      if (edited.currency !== orig.currency) {
        changes.push(`currency ${orig.currency} → ${edited.currency}`);
      }
      if (changes.length > 0) diffs.push({ idx, changes });
    });
    return diffs;
  }, [editedRows, latestProposalBlock]);

  const hasEdits = editsSummary.length > 0;

  // ── Handlers ──
  const handleIngestAll = useCallback(() => {
    if (!active || !latestProposalBlock || !editedRows.length || pending) return;
    const pageKey = pageKeyRef.current;
    if (!pageKey) return;
    const rowsJson = JSON.stringify(editedRows, null, 2);
    const message = (
      `${INGEST_ALL_CONFIRMED_PREFIX}${pageKey}\n\n`
      + `Use these final edited rows for commit_bookings (page_key=${pageKey}). `
      + 'Call commit_bookings with confirm=true and offer_id="" '
      + '(text-based draft, no source offer).\n\n'
      + '```json bookings-final\n'
      + `${rowsJson}\n`
      + '```'
    );
    if (typeof scrollToLatest === 'function') scrollToLatest('auto');
    send(message);
  }, [active, latestProposalBlock, editedRows, pending, send, scrollToLatest]);

  const handleDiscussChanges = useCallback(() => {
    if (!active || !latestProposalBlock || !editedRows.length || pending) return;
    const rowsJson = JSON.stringify(editedRows, null, 2);
    const editLines = hasEdits
      ? editsSummary.map((d) => `- Row ${d.idx}: ${d.changes.join('; ')}`).join('\n')
      : '_No edits yet — please review the draft and suggest improvements._';
    const message = (
      'I made the following edits to your draft. Please look them over '
      + 'and either acknowledge (re-emit a fresh `bookings-proposal` block '
      + 'with my edits applied) or suggest refinements before I ingest.\n\n'
      + `${editLines}\n\n`
      + 'Current rows:\n\n'
      + '```json bookings-current\n'
      + `${rowsJson}\n`
      + '```'
    );
    if (typeof scrollToLatest === 'function') scrollToLatest('auto');
    send(message);
  }, [
    active, latestProposalBlock, editedRows, pending, send,
    editsSummary, hasEdits, scrollToLatest,
  ]);

  return {
    proposal: latestProposalBlock ? latestProposalBlock.proposal : null,
    rows: editedRows,
    onRowEdit: handleRowEdit,
    onIngestAll: handleIngestAll,
    onDiscussChanges: handleDiscussChanges,
    hasEdits,
    commitResult: latestCommitResult,
    fieldWarnings: latestProposalBlock ? latestProposalBlock.fieldWarnings : null,
  };
}
